import { z } from 'zod';
/**
 * A preference of the signed-in user, never of the practice — kept out of
 * `practice_settings` on purpose (`docs/data-model.md`). `theme` is the first one;
 * a display setting like a per-view column list belongs here too, as its
 * own optional key, never as a new column.
 */
/**
 * English, like every other identifier here — `schiefer`, `blau`, `salbei` and
 * `nacht` until the migration squash, which was the last moment a rename was
 * free: these are *values inside* `app_user.preferences`, so after go-live
 * changing them would mean a data migration over everybody's settings for the
 * sake of a name. `rose` reads the same in both languages and did not move.
 *
 * The German labels live in `apps/web/src/lib/strings.ts`, keyed by these, and
 * `apps/web/index.html` repeats the list because nothing can be imported that
 * early — `theme-options.test.ts` in the web package asserts the two agree.
 */
export const themeOptions = ['slate', 'blue', 'sage', 'rose', 'night'];
export const themeSchema = z.enum(themeOptions);
/** Where to land after signing in. */
export const startPageOptions = ['overview', 'contacts', 'calendar', 'activities'];
export const startPageSchema = z.enum(startPageOptions);
/**
 * One schema for both reading and writing: every key is optional by nature —
 * a preference that was never set is simply absent, not a distinct "unset"
 * value — so a `PATCH` body and a `GET` response have the same shape. Unknown
 * keys are dropped on parse rather than rejected, which is what lets an older
 * client read a `preferences` blob a newer one has already added a key to.
 *
 * **Every preference is its own flat, top-level key — never nested.**
 * `updateUserPreferences` (`apps/server/src/domain/user-preferences.ts`)
 * merges a save with Postgres's `jsonb || jsonb`, and that merge is shallow:
 * it replaces a key wholesale rather than merging into it. A key like
 * `columns: { contacts: [...], invoices: [...] }` would lose the invoice
 * columns the instant the contact list saves its own — silently, and not
 * noticed until both lists have been customized. `contactListColumns` and
 * `invoiceListColumns` as two separate top-level keys cannot make that
 * mistake; neither can ever overwrite the other.
 */
export const userPreferencesSchema = z.object({
    theme: themeSchema.optional(),
    startPage: startPageSchema.optional(),
    sidebarCollapsed: z.boolean().optional(),
    /** Column keys, visible ones in display order — membership is visibility,
     *  array order is column order (D6, `ColumnPicker`). Every column the list
     *  offers is in here and nothing decides visibility beside it; the note
     *  that used to stand here, exempting the appointment column because the
     *  Aktuell/A–Z view owned it, went with that view in L3. */
    contactListColumns: z.array(z.string()).optional(),
    /** The same shape for the invoice list under Zahlungen (D7) — its own flat
     *  key, never nested with the one above; see the paragraph on the merge. */
    invoiceListColumns: z.array(z.string()).optional(),
});
//# sourceMappingURL=user-preferences.js.map